import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import PurchaseDataModal from 'components/AI/PurchaseDataModal'
import ThanxAISuggestionChip from 'components/AI/ThanxAISuggestionChip'
import ArrowRight from 'components/Icons/ArrowRight'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import type {
  Callout,
  CalloutKind,
  CalloutLink,
  CalloutPurchaseDataLink,
} from 'slices/aiConversations/calloutTypes'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { PERMISSION_MAP } from 'utilities/permissionMap'

const t = buildTranslate('components.ai.callout')

const LINK_COLUMN_GAP = '16px'
const LINK_ROW_GAP = '4px'
const ACTION_GAP = '8px'

// A modal link reads like the anchors next to it; these overrides replace the minimal kind's own
const MODAL_LINK_OVERRIDES = {
  BaseButton: {
    style: {
      backgroundColor: 'transparent',
      ':hover': { backgroundColor: 'transparent' },
      ':active': { backgroundColor: 'transparent' },
      height: 'auto',
      paddingTop: '0px',
      paddingRight: '0px',
      paddingBottom: '0px',
      paddingLeft: '0px',
      borderTopWidth: '0px',
      borderRightWidth: '0px',
      borderBottomWidth: '0px',
      borderLeftWidth: '0px',
    },
  },
}

type NotificationKind = React.ComponentProps<typeof Notification>['kind']

const NOTIFICATION_KIND: Record<CalloutKind, NotificationKind> = {
  info: 'info',
  warning: 'warning',
  error: 'negative',
}

// The location-level reports subject, whatever the payload asks for
const PURCHASE_DATA_SUBJECT = 'LocationReports'

// An unknown resource fails closed: the backend asked for a check we can't make
const canViewLink = (
  ability: ReturnType<typeof useAbility>,
  link: CalloutLink
) => {
  if ('modal' in link) {
    return Boolean(ability?.can('access', PURCHASE_DATA_SUBJECT))
  }
  const { permission } = link
  if (!permission) return true
  if (!Object.prototype.hasOwnProperty.call(PERMISSION_MAP, permission)) {
    return false
  }
  return PERMISSION_MAP[permission].view.every(([action, subject]) =>
    Boolean(ability?.can(action, subject))
  )
}

const LinkLabel = ({ label }: { label: string }) => (
  <>
    <Text.BodyText4 bold color="belizeHole50">
      {label}
    </Text.BodyText4>
    <ArrowRight className="ml-xxs" />
  </>
)

export interface ThanxAICalloutProps {
  callout: Callout
  onActionClick?: (message: string) => void
  actionsDisabled?: boolean
}

const ThanxAICallout = ({
  callout,
  onActionClick,
  actionsDisabled = false,
}: ThanxAICalloutProps) => {
  const [css] = useStyletron()
  const ability = useAbility()
  const [openLink, setOpenLink] = useState<CalloutPurchaseDataLink | null>(null)
  const { kind, title, body, actions } = callout
  const links = callout.links.filter(link => canViewLink(ability, link))
  const showActions = actions.length > 0 && Boolean(onActionClick)

  return (
    <div className="my-xs">
      <Notification kind={NOTIFICATION_KIND[kind]} size="full">
        {title && (
          <Text.BodyText4 bold tag="div">
            {title}
          </Text.BodyText4>
        )}
        {body && <Text.BodyText4 tag="div">{body}</Text.BodyText4>}
        {links.length > 0 && (
          <div
            className={`d-flex flex-wrap mt-xs ${css({
              columnGap: LINK_COLUMN_GAP,
              rowGap: LINK_ROW_GAP,
            })}`}
          >
            {/* The payload's lists never change, so the index is a stable key */}
            {links.map((link, index) => {
              if ('modal' in link) {
                return (
                  <Button
                    key={index}
                    kind="minimal"
                    aria-haspopup="dialog"
                    overrides={MODAL_LINK_OVERRIDES}
                    onClick={() => setOpenLink(link)}
                  >
                    <LinkLabel label={link.label} />
                  </Button>
                )
              }
              // A new tab keeps the conversation open
              const anchorProps = {
                target: '_blank',
                rel: 'noopener noreferrer',
                'aria-label': t('link_aria_label', { label: link.label }),
                className: 'd-inline-flex align-items-center',
              }
              return 'to' in link ? (
                <Link key={index} to={link.to} {...anchorProps}>
                  <LinkLabel label={link.label} />
                </Link>
              ) : (
                <a key={index} href={link.href} {...anchorProps}>
                  <LinkLabel label={link.label} />
                </a>
              )
            })}
          </div>
        )}
        {showActions && (
          <div className={`d-flex flex-wrap mt-xs ${css({ gap: ACTION_GAP })}`}>
            {actions.map((action, index) => (
              <ThanxAISuggestionChip
                key={index}
                suggestion={action.label}
                onClick={() => onActionClick?.(action.label)}
                disabled={actionsDisabled}
                inline={true}
              />
            ))}
          </div>
        )}
      </Notification>
      {openLink && (
        <PurchaseDataModal
          months={openLink.months}
          onClose={() => setOpenLink(null)}
        />
      )}
    </div>
  )
}

export default ThanxAICallout
